import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from "@mui/lab";
import { Paper, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useEffect, useState } from "react";
import { AppId } from "../../../app/appTypes";
import { loadUrsOfferEventsHistory, UrsOfferEvents } from "../ursOfferSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { selectSettings, Locale } from "../../settings/settingsSlice";
import { Localized } from "@fluent/react";

type EventConfig = {
  label: Record<Locale, string>;
  color: "grey" | "primary" | "success" | "error" | "warning";
};

export const UrsOfferEventHistoryView = ({ documentId }: { documentId: AppId }) => {
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [error, setError] = useState(null);
  const [events, setEvents] = useState<UrsOfferEvents[]>([]);
  const { locale } = useAppSelector(selectSettings);

  useEffect(() => {
    setIsLoading(true)
    dispatch(loadUrsOfferEventsHistory(documentId))
      .then(unwrapResult)
      .then(setEvents)
      .catch((error) => { console.error(error); setIsError(true); setError(error); })
      .finally(() => setIsLoading(false))
  }, [])


  const getEventData = (eventType: string, locale: Locale) => {
    const cfg = eventConfig[eventType];
    if (!cfg) {
      return { label: eventType, color: "grey" as const };
    }
    return { label: cfg.label[locale], color: cfg.color };
  };

  if (isError) {
    return <>
      <ErrorContainer error={error} />
    </>
  }

  if (isLoading) {
    return <>
      <LoadingContainer />
    </>
  }


  return (
    <Paper sx={{ p: 3, borderRadius: "16px" }}>
      <Typography variant="h6" gutterBottom>
        <Localized id="urs-offer-event-history"><span>Historia oferty</span></Localized>
      </Typography>

      <Timeline position="alternate">
        {events.map((e, idx) => {
          const { label, color } = getEventData(e.eventType, locale);

          return (
            <TimelineItem key={e.id}>
              <TimelineOppositeContent color="text.secondary">
                <Typography variant="body2">{e.createdBy}</Typography>
                <Typography variant="body2">
                  {new Date(e.eventDate).toLocaleString()}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color={color} />
                {idx < events.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="body1">{label}</Typography>
                {e.description && (
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", color: "text.secondary", mt: 0.5 }}
                  >
                    “{e.description}”
                  </Typography>
                )}
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Paper>
  );
}

const eventConfig: Record<string, EventConfig> = {
  ACTION_SEND_TO_EVALUATION: {
    label: {
      "pl": "Wysłano do klienta do oceny",
      "en-US": "Sent to client for evaluation",
      "de": "Zur Bewertung an den Kunden gesendet",
    },
    color: "primary",
  },
  ACTION_FINISHED_COMMENTING: {
    label: {
      "pl": "Komentowanie zakończone",
      "en-US": "Commenting finished",
      "de": "Kommentierung abgeschlossen",
    },
    color: "success",
  },
  ACTION_REJECTED: {
    label: {
      "pl": "Zrezygnowano z komentowania URS",
      "en-US": "Resigned from commenting on URS",
      "de": "Auf die Kommentierung der URS verzichtet",
    },
    color: "error",
  },
  ACTION_BACK_FROM_EVALUATION: {
    label: {
      "pl": "Oferta wróciła z oceny",
      "en-US": "Offer returned from evaluation",
      "de": "Angebot aus der Bewertung zurückgekehrt",
    },
    color: "grey",
  },
};